<template>
  <div id="transaction">
    <div class="container">

      <PageTitle :title="`${$t('loyalty_transactions.single')} #${transaction ? transaction.id : ''}`"></PageTitle>

      <Tabs>
        <TabSection :title="$t('loyalty_transactions.details')" v-if="transaction && loyaltyAccount" collapsible>
          <div class="columns">
            <div class="column">

              <div class="group">
                <label>{{ $t('loyalty_transactions.number') }}</label>
                <div class="value">#{{ transaction.id }}</div>
              </div>

              <div class="group">
                <label>{{ $t('loyalty_transactions.date') }}</label>
                <div class="value">{{ moment(transaction.createdAt).format('YYYY/MM/DD HH:mm') }}</div>
              </div>

              <div class="group" v-if="Math.abs(transaction.points) > 0.0001">
                <label>{{ $t('loyalty_transactions.points') }}</label>
                <div class="value">{{ transaction.points }}</div>
              </div>

              <div class="group" v-if="Math.abs(transaction.money) > 0.0001">
                <label>{{ $t('loyalty_transactions.amount') }}</label>
                <div class="value">{{ $tc(transaction.money, transaction.currency) }}</div>
              </div>

              <div class="group">
                <label>{{ $t('loyalty_transactions.type') }}</label>
                <div class="value">{{ $t('loyalty_transactions.' + transaction.type) }}</div>
              </div>

              <template v-if="moreDataShown">
                <div class="group" v-for="[key, val] in Object.entries(moreDataShown)">
                  <label>{{ $t(`loyalty_transactions.${key}`) }}</label>
                  <div class="value">{{ val }}</div>
                </div>
              </template>

            </div>

            <div class="column">

              <div class="group">
                <label>{{ $t('loyalty_transactions.status') }}</label>
                <div class="value">
                  <div class="color" :style="{ backgroundColor: getStatusColor(transaction) }"></div>
                  {{ $t("loyalty_transactions." + getStatus(transaction)) }}
                </div>
              </div>

              <div class="group external-link" v-if="transaction.createdBy">
                <router-link :to="$router.buildContext('/customer', { id: transaction.createdBy.id })"
                  v-if="transaction.type !== 'adjust'">
                  <label>{{ $t('loyalty_transactions.createdBy') }}</label>
                  <div class="value">
                    <svgicon class="icon" src="/icon/user.svg" :height="15" :width="15" />
                    <span class="content">{{ transaction.createdBy.username }}</span>
                    <svgicon class="external" src="/icon/link.svg" :height="25" :width="25" />
                  </div>
                </router-link>
                <div v-else>
                  <label>{{ $t('loyalty_transactions.createdBy') }}</label>
                  <div class="value">
                    <svgicon class="icon" src="/icon/user.svg" :height="15" :width="15" />
                    <span class="content">{{ transaction.createdBy.username }}</span>
                  </div>
                </div>
              </div>

              <div class="group external-link" v-if="loyaltyAccount">
                <router-link :to="$router.buildContext('/loyalty-account', { id: transaction.account.id })">
                  <label>{{ $t('loyalty_transactions.loyalty_account') }}</label>
                  <div class="value">
                    <svgicon class="icon" src="/icon/wallet.svg" :height="15" :width="15" />
                    <span class="content">{{ loyaltyAccount.description || `#${loyaltyAccount.id}` }}</span>
                    <svgicon class="external" src="/icon/link.svg" :height="25" :width="25" />
                  </div>
                </router-link>
              </div>

              <div class="group external-link" v-if="transaction.sale">
                <router-link :to="$router.buildContext('/sale', { id: transaction.sale })">
                  <label>{{ $t('loyalty_transactions.sale') }}</label>
                  <div class="value">
                    <svgicon class="icon" src="/icon/sales.svg" :height="15" :width="15" />
                    <span class="content">{{ `#${transaction.sale}` }}</span>
                    <svgicon class="external" src="/icon/link.svg" :height="25" :width="25" />
                  </div>
                </router-link>
              </div>

            </div>
          </div>
        </TabSection>
      </Tabs>

      <div class="footer-actions">
        <button class="btn btn-primary" @click="back">{{ $t('back') }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import moment from "moment-timezone";
  import PageTitle from "@/components/PageTitle";

  export default {
    name: "Transaction",

    components: { PageTitle },

    data() {
      return {
        transaction: null,
        loyaltyAccount: null,
        moment
      }
    },

    computed: {
      moreDataShown() {
        /* show some fields from moreData */
        let fieldsOrdered = ['company', 'reasonCode', 'note'];
        return Object.entries(this.transaction.moreData || {})
          .filter(([k, _]) => fieldsOrdered.includes(k))
          .map(([k, v]) => {
            if (v && v.name) {
              v = v.name;
            } else if (v && k === 'reasonCode') {
              v = this.$t(`loyalty_transactions.reason_codes.${v}`)
            }
            return [k, v]
          })
          .sort((a1, a2) => fieldsOrdered.indexOf(a1[0]) < fieldsOrdered.indexOf(a2[0]) ? -1 : 1)
          .reduce((acc, [k, v]) => Object.assign(acc, { [k]: v }), {})
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "loyalty_transactions.single";
      this.$store.state.pageIcon = "/icon/loyalty.svg";

      if (this.$route.query.id) {
        this.loadTransaction(this.$route.query.id);
      }
    },

    methods: {

      ...mapActions(["getLoyaltyTransaction", "getLoyaltyAccount"]),

      async loadTransaction(id) {
        let transaction = await this.getLoyaltyTransaction(id);
        if (transaction) {
          this.transaction = transaction;
          let account = await this.getLoyaltyAccount(transaction.account.id);
          if (account) {
            this.loyaltyAccount = account;
          } else {
            toast(this.$t("error.no_server"), "error");
          }

        } else {
          toast(this.$t("error.no_server"), "error");
        }
      },

      back() {
        this.$router.push(this.$router.buildContext("/loyalty-transactions"));
      },

      getStatus(transaction) {
        if (transaction.cancelled) {
          return "cancelled";
        }
        if (transaction.refused) {
          return "refused";
        }
        if (transaction.pending) {
          return "pending";
        }
        return "completed";
      },

      getStatusColor(transaction) {
        if (transaction.cancelled || transaction.refused) {
          return "#f12020";
        }
        if (transaction.pending) {
          return "#ffc800";
        }
        return "#4cca4c";
      }

    }
  }
</script>

<style lang="scss" scoped>
  #transaction {
    display: flex;
  }

  .color {
    width: 15px;
    height: 15px;
    border-radius: 15px;
    margin-right: 10px;
  }

  .value {
    display: flex;
    align-items: center;
    font-size: 18px;
  }

  .icon {
    margin-right: 10px;
    fill: #c3c3c3;
  }

  .group {
    margin-bottom: 15px;
  }

  .btnAccount {
    display: flex;
    align-items: center;
    fill: white;

    .svgicon {
      margin-left: 10px;
    }
  }

  .external-link {
    .external {
      fill: var(--primary-color);
    }

    a {
      color: inherit;
      text-decoration: none;
    }

    .content {
      flex-grow: 1;
    }
  }
</style>